import type { BeforeSendFn } from "posthog-js";

// Properties posthog-js fills with full URLs. Portal URLs can carry onboarding
// query/hash params (tenant/client ids, temporary credentials), so only
// origin + path may leave the browser.
const URL_KEYS = new Set([
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
]);

/** Strip query string and fragment from an absolute URL. */
export function stripUrl(value: string): string {
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value;
  }
}

function scrub(props: Record<string, unknown> | undefined) {
  if (!props) return;
  for (const key of URL_KEYS) {
    if (typeof props[key] === "string") props[key] = stripUrl(props[key]);
  }
  if (props.$heatmap_data) props.$heatmap_data = scrubHeatmapKeys(props.$heatmap_data);
}

/**
 * posthog-js keys heatmap points by the full location.href, which the URL
 * properties above do not cover. Merge points whose stripped URLs collide.
 */
function scrubHeatmapKeys(data: unknown): unknown {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const out: Record<string, unknown[]> = {};
  for (const [url, points] of Object.entries(data)) {
    if (!Array.isArray(points)) continue;
    const key = stripUrl(url);
    out[key] = [...(out[key] ?? []), ...points];
  }
  return out;
}

/** posthog-js `before_send` hook removing query/hash from URL properties. */
export const scrubUrls: BeforeSendFn = (event) => {
  if (!event) return event;
  scrub(event.properties);
  scrub(event.$set);
  scrub(event.$set_once);
  return event;
};
